import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    CircularProgress,
    TextField,
    Pagination,
    MenuItem,
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    Agriculture as AgricultureIcon,
    Grain as CropTypeIcon,
    Public as TotalAreaIcon,
    WbSunny as SoilTypeIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Place as LocationIcon,
    CalendarToday as DateIcon,
    Info as InfoIcon,
    Timeline as CropStageIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CiMoneyCheck1 } from "react-icons/ci";
// Ensure these API utility functions are correctly implemented and handle tokens
import { getWithTokenAPI, deleteAPI, putAPI } from "../../utils/Api";

const FarmPrecisionPage = () => {
    const [farmData, setFarmData] = useState([]);
    console.log(farmData, "farmData")
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [farmsPerPage] = useState(10); // Number of orders per page

    const [selectedFarmDetails, setSelectedFarmDetails] = useState(null);
    console.log(selectedFarmDetails , "selectedFarmDetails")
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [farmToDelete, setFarmToDelete] = useState(null);
    const [updateFormData, setUpdateFormData] = useState({});

    console.log(updateFormData, "updateFormData")
    const permissions = JSON.stringify(localStorage.getItem("permissions"));

    // Ref to track if the component is mounted to prevent state updates on unmounted component
    const isMounted = useRef(true);

    // Theme colors
    const primaryGreen = "#DC3545";
    const lightGreen = "#ffe5e5";
    const darkGreen = "#b02a37";
    const secondaryTextColor = "rgba(0, 0, 0, 0.6)"; // Consistent with newsletter table header text color

    // --- API CALLS ---

    // Fetch all farms
    const fetchAllFarms = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        setError(null);

        try {
            const res = await getWithTokenAPI(token, "/precision/find_all");

            if (
                res.statusCode === 200 &&
                res.data &&
                Array.isArray(res.data.precision_managements)
            ) {
                const formattedData = res.data.precision_managements.map((farm) => ({
                    _id: farm._id,
                    farm_id: farm.farm_id,
                    name: farm.name || `Farm ${farm.farm_id || "Unknown"}`,
                    area: farm.area,
                    total_area: `${farm.area || 0} acres`,
                    soil_type: farm.soil_type || "N/A",
                    total_amount: farm.total_amount || 0,
                    tax_amount: farm.tax_amount || 0,
                    crop_type: farm.croptype
                        ? String(farm.croptype)
                            .split(",")
                            .map((c) => c.trim())
                            .filter((c) => c !== "")
                        : [],
                    farm_image:
                        farm.farm_image ||
                        "https://via.placeholder.com/150/00b207/FFFFFF?text=Farm",
                    details: {
                        location: `${farm.address_id.street || ""}, ${farm.address_id.city || ""}, ${farm.address_id.state || ""}, ${farm.address_id.country || ""} - ${farm.address_id.zip_code || "N/A"}`,
                        sowingdate: farm.sowingdate || "N/A",
                        status: farm.Booking_status || "N/A",
                        crop_stage: farm.crop_stage || "N/A",
                        category: farm.category || "N/A",
                        variety: farm.variety || "N/A",
                        expired_date: farm.Expired_Date || "N/A",
                    },
                }));
                console.log(
                    "Formatted Farm Data for UI (check farm_id here):",
                    formattedData
                );
                setFarmData(formattedData);
            } else {
                toast.error(
                    res.message ||
                    "Failed to fetch farms due to an unexpected response or empty data."
                );
                setFarmData([]);
            }
        } catch (err) {
            console.error("Error fetching farm data:", err);
            setError(err.message);
            toast.error(`Error fetching farms: ${err.message}`);
            setFarmData([]);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Pagination logic
    const indexOfLastSubscription = currentPage * farmsPerPage;
    const indexOfFirstSubscription = indexOfLastSubscription - farmsPerPage;
    const farmdata = farmData.slice(
        indexOfFirstSubscription,
        indexOfLastSubscription
    );
    const totalPages = Math.ceil(farmData.length / farmsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Fetch single farm details (used for view dialog to get comprehensive details)
    const fetchFarmDetails = async (farmId) => {
        const token = localStorage.getItem("token");
        if (!farmId) {
            console.error("fetchFarmDetails: farmId is missing.");
            return null;
        }
        try {
            // Ensure the endpoint matches your backend for fetching one farm
            const res = await getWithTokenAPI(
                token,
                `/precision/find_one?farmID=${farmId}`
            );
            console.log(
                `API Response for /precision/find_one (Farm ID: ${farmId}):`,
                res
            );
            if (res.statusCode === 200 && res.data) {
                return res.data;
            }
            return null;
        } catch (err) {
            console.error(`Error fetching farm ${farmId} details:`, err);
            toast.error(
                `Error fetching details for Farm ID ${farmId}: ${err.message}`
            );
            return null;
        }
    };

    // Handle delete farm operation
    const handleDeleteFarm = async () => {
        if (!farmToDelete || !farmToDelete.farm_id) {
            // Use farm_id for deletion
            toast.error("No valid farm selected for deletion.");
            return;
        }

        const token = localStorage.getItem("token");
        setLoading(true);
        try {
            // Ensure your backend expects 'farmID' in the request body for delete
            const res = await deleteAPI(token, "/precision/delete", {
                farmID: farmToDelete.farm_id,
            });
            console.log("Delete API Response:", res);

            if (res.statusCode === 200) {
                toast.success(res.message || "Farm deleted successfully!");
                await fetchAllFarms(); // Refresh UI after successful deletion
            } else {
                toast.error(res.message || "Failed to delete farm.");
            }
        } catch (err) {
            console.error("Error deleting farm:", err);
            toast.error(`Error deleting farm: ${err.message}`);
        } finally {
            if (isMounted.current) {
                setLoading(false);
                setOpenDeleteConfirm(false);
                setFarmToDelete(null);
            }
        }
    };

    // Handle update farm operation
    const handleUpdateFarm = async () => {
        console.log(
            "Current updateFormData state (before validation):",
            updateFormData
        );

        if (!updateFormData._id) {
            toast.error("Farm ID (farm_id) missing for update. Cannot proceed.");
            console.error(
                "Error: updateFormData.farm_id is missing from state.",
                updateFormData
            );
            return;
        }

        const token = localStorage.getItem("token");
        setLoading(true);
        try {
            if (!token) {
                throw new Error("Authentication token not found. Please log in.");
            }

            const payload = {
                _id: updateFormData._id,
                croptype: String(updateFormData.crop_type || "")
                    .split(",")
                    .map((c) => c.trim())
                    .filter((c) => c !== "")
                    .join(","),
                sowingdate: updateFormData.details?.sowingdate || "",
                name: updateFormData.name || "",
                area: parseFloat(
                    String(updateFormData.total_area || "0").replace(" acres", "")
                ),
                soil_type: updateFormData.soil_type || "",
                District: updateFormData.details?.location?.split(",")[0]?.trim() || "",
                State: updateFormData.details?.location?.split(",")[1]?.trim() || "",
                Booking_status: updateFormData.details?.status || "",
                crop_stage: updateFormData.details?.crop_stage || "",
                category: updateFormData.details?.category || "",
                variety: updateFormData.details?.variety || "",
                Expired_Date: updateFormData.details?.expired_date || "",
            };

            Object.keys(payload).forEach((key) => {
                const value = payload[key];
                if (
                    value === undefined ||
                    value === null ||
                    (typeof value === "string" && value.trim() === "")
                ) {
                    delete payload[key];
                }
                if (typeof value === "number" && isNaN(value)) {
                    delete payload[key];
                }
            });

            console.log("Update Payload being sent to API:", payload);

            const res = await putAPI(token, `/precision-new/update`, payload);
            console.log("Update API Response:", res);

            if (res.statusCode === 200) {
                toast.success(res.message || "Farm updated successfully!");
                setOpenUpdateDialog(false);
                setUpdateFormData({}); // Clear form data after successful update
                await fetchAllFarms(); // Refresh the list after update
            } else {
                toast.error(res.message || "Failed to update farm.");
            }
        } catch (err) {
            console.error("Error updating farm:", err);
            toast.error(`Error updating farm: ${err.message}`);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // --- DIALOG HANDLERS ---

    // View farm details
    const handleViewFarm = async (farm) => {
        console.log(farm , "farm")
        setSelectedFarmDetails(farm);
        setOpenViewDialog(true);
        setLoading(true);
        console.log("farm========!.", farm)
        try {
            const detailedData = await fetchFarmDetails(farm.farm_id);
            // console.log(detailedData , "detailedData")
            if (detailedData) {
                setSelectedFarmDetails((prev) => ({
                    ...prev,
                    details: {
                        ...prev.details,
                        location: `${detailedData.District || "N/A"}, ${detailedData.State || "N/A"
                            }`,
                        sowingdate: detailedData.sowingdate || "N/A",
                        status: detailedData.Status || "N/A",
                        crop_stage: detailedData.crop_stage || "N/A",
                        category: detailedData.category || "N/A",
                        variety: detailedData.variety || "N/A",
                        expired_date: detailedData.Expired_Date || "N/A",
                        total_amount: detailedData.total_amount || "N/A",
                    },
                }));
            } else {
            }
        } catch (error) {
            console.error("Error during detailed farm view fetch:", error);
            toast.error("Failed to load full farm details.");
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleOpenUpdateDialog = (farm) => {
        console.log(
            "Farm object passed to handleOpenUpdateDialog (check farm_id here):",
            farm
        );

        setUpdateFormData({
            ...farm,
            crop_type: Array.isArray(farm.crop_type)
                ? farm.crop_type.join(", ")
                : farm.crop_type || "",
            details: {
                ...farm.details,
            },
        });
        setOpenUpdateDialog(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setUpdateFormData((prev) => {
            if (
                [
                    "sowingdate",
                    "status",
                    "crop_stage",
                    "category",
                    "variety",
                    "expired_date",
                    "location",
                ].includes(name)
            ) {
                return {
                    ...prev,
                    details: {
                        ...prev.details,
                        [name]: value,
                    },
                };
            }
            // For top-level fields like name, area, soil_type, crop_type, farm_id
            return {
                ...prev,
                [name]: value,
            };
        });
    };

    // --- EFFECT HOOKS ---
    useEffect(() => {
        fetchAllFarms();
        return () => {
            isMounted.current = false;
        };
    }, []);

    // --- RENDER JSX ---
    return (
        <Box sx={{ p: 3, width: "100%" }}>
            <ToastContainer position="top-center" autoClose={1500} />

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                    p: 2,
                    backgroundColor: lightGreen,
                    borderRadius: 2,
                    borderLeft: `5px solid ${primaryGreen}`,
                }}
            >
                <AgricultureIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
                <Box>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ color: primaryGreen, fontWeight: "bold" }}
                    >
                        Farm Precision
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        Manage all registered farms
                    </Typography>
                </Box>
            </Box>

            {/* Main Content - Updated Table Design */}
            <div className="table-container border rounded">
                <div className="table-responsive">
                    {loading && farmData.length === 0 && !error ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                p: 4,
                                alignItems: "center",
                                height: "200px" // Added for consistent loading indicator height
                            }}
                        >
                            <CircularProgress sx={{ color: primaryGreen }} />
                            <Typography sx={{ ml: 2, color: primaryGreen }}>
                                Loading farms...
                            </Typography>
                        </Box>
                    ) : error ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 4,
                                textAlign: "center",
                                color: "error.main",
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Error Loading Farms
                            </Typography>
                            <Typography variant="body2">{error}</Typography>
                            <Button
                                onClick={fetchAllFarms}
                                variant="contained"
                                sx={{
                                    mt: 2,
                                    backgroundColor: primaryGreen,
                                    "&:hover": { backgroundColor: darkGreen },
                                }}
                            >
                                Retry
                            </Button>
                        </Box>
                    ) : farmData.length > 0 ? (
                        <>
                            <table className="table m-0 resource-head">
                                <thead
                                    className="sticky-header fs-6"
                                    style={{ backgroundColor: lightGreen }}
                                >
                                    <tr>
                                        <th
                                            className="table-header"
                                            style={{ color: secondaryTextColor, textAlign: "left", width: "10%" }}
                                        >
                                            S.No
                                        </th>
                                        <th
                                            className="table-header"
                                            style={{ color: secondaryTextColor, textAlign: "left", width: "20%" }}
                                        >
                                            Farm Name
                                        </th>
                                        <th
                                            className="table-header"
                                            style={{ color: secondaryTextColor, textAlign: "left", width: "20%" }}
                                        >
                                            Total Area
                                        </th>
                                        <th
                                            className="table-header"
                                            style={{ color: secondaryTextColor, textAlign: "left", width: "20%" }}
                                        >
                                            Total Amount (Ksh)
                                        </th>

                                        <th
                                            className="table-header"
                                            style={{ color: secondaryTextColor, textAlign: "left", width: "20%" }}
                                        >
                                            Crop Type
                                        </th>
                                        <th
                                            className="table-header"
                                            style={{ color: secondaryTextColor, textAlign: "left", width: "10%" }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {farmdata.map((farm, index) => (
                                        <TableRow key={farm._id || `farm-${index}`} hover>
                                            <TableCell sx={{ textAlign: "left" }}>{(currentPage - 1) * farmsPerPage + index + 1}</TableCell>
                                            {/* <TableCell sx={{ textAlign: "left" }}>
                                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                    {farm.name}
                                                </Typography>
                                            </TableCell> */}

                                            <TableCell sx={{ textAlign: "left" }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: primaryGreen,
                                                        textDecoration: 'none',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => handleViewFarm(farm)}
                                                >
                                                    {farm.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "left" }}>{farm.total_area}</TableCell>
                                            <TableCell sx={{ textAlign: "left" }}>{farm.total_amount}</TableCell>


                                            <TableCell sx={{ textAlign: "left" }}>
                                                {farm.crop_type && farm.crop_type.length > 0 ? (
                                                    <Box
                                                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                                                    >
                                                        {farm.crop_type.map((crop, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={crop}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: lightGreen,
                                                                    color: darkGreen,
                                                                    fontWeight: "bold",
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    "N/A"
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "left" }}>
                                                <Box
                                                    sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                                                >
                                                    <IconButton
                                                        onClick={() => handleViewFarm(farm)}
                                                        sx={{ color: primaryGreen }}
                                                        aria-label="view farm"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                    {permissions.includes("update_farmdata") && (
                                                        <IconButton
                                                            onClick={() => handleOpenUpdateDialog(farm)}
                                                            sx={{ color: primaryGreen }}
                                                            aria-label="edit farm"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    )}
                                                    {/* If you re-enable delete, uncomment this block */}
                                                    {/* <IconButton
                                                        onClick={() => {
                                                            setFarmToDelete(farm);
                                                            setOpenDeleteConfirm(true);
                                                        }}
                                                        sx={{ color: "#f44336" }}
                                                        aria-label="delete farm"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton> */}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 4,
                                textAlign: "center",
                            }}
                        >
                            <AgricultureIcon
                                sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
                            />
                            <Typography variant="h6" sx={{ mb: 1, color: "text.disabled" }}>
                                No farm precision bookings found
                            </Typography>
                        </Box>
                    )}
                </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "end",
                        mt: 3,
                        pb: 2,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                color: primaryGreen,
                            },
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: primaryGreen,
                                color: "white",
                            },
                        }}
                    />
                </Box>
            )}

            {/* View Farm Details Dialog */}
            <Dialog
                open={openViewDialog}
                onClose={() => setOpenViewDialog(false)}
                maxWidth="md"
                fullWidth
            >
                {selectedFarmDetails && (
                    <>
                        <DialogTitle
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: primaryGreen,
                                color: "white",
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: "bold",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <AgricultureIcon sx={{ mr: 1 }} /> Farm Details:{" "}
                                {selectedFarmDetails.name}
                            </Typography>
                            <IconButton
                                aria-label="close"
                                onClick={() => setOpenViewDialog(false)}
                                sx={{ color: "white" }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: 3 }}>
                            {loading && !selectedFarmDetails.details?.location ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 4,
                                        alignItems: "center",
                                    }}
                                >
                                    <CircularProgress sx={{ color: primaryGreen }} />
                                    <Typography sx={{ ml: 2, color: "text.secondary" }}>
                                        Loading farm details...
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${lightGreen}`,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    mb: 2,
                                                    color: darkGreen,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <AgricultureIcon sx={{ mr: 1 }} /> General Information
                                            </Typography>
                                            <Box
                                                sx={{ display: "flex", alignItems: "center", mb: 2 }}
                                            >
                                                <Avatar
                                                    src={selectedFarmDetails.farm_image}
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        mr: 2,
                                                        bgcolor: primaryGreen,
                                                    }}
                                                    variant="square"
                                                >
                                                    {selectedFarmDetails.name?.charAt(0) || "F"}
                                                </Avatar>
                                                <Box>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{ fontWeight: "bold", color: primaryGreen }}
                                                    >
                                                        {selectedFarmDetails.name}
                                                    </Typography>
                                                    {/* <Typography
                                                        variant="body2"
                                                        sx={{ color: "text.secondary" }}
                                                    >
                                                        Farm ID: {selectedFarmDetails.farm_id}
                                                    </Typography> */}
                                                </Box>
                                            </Box>
                                            <Box sx={{ pl: 2 }}>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <TotalAreaIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Total Area:
                                                        </span>{" "}
                                                        {selectedFarmDetails.total_area}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <CropTypeIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Crop Type(s):
                                                        </span>{" "}
                                                        {selectedFarmDetails.crop_type?.join(", ") || "N/A"}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <LocationIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Location:
                                                        </span>{" "}
                                                        {selectedFarmDetails.details?.location}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${lightGreen}`,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    mb: 2,
                                                    color: darkGreen,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <AgricultureIcon sx={{ mr: 1 }} /> Additional Details
                                            </Typography>
                                            <Box sx={{ pl: 2 }}>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <DateIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Sowing Date:
                                                        </span>{" "}
                                                        {/* {selectedFarmDetails.details?.sowingdate} */}
                                                        {selectedFarmDetails.details?.sowingdate
                                                            ? selectedFarmDetails.details?.sowingdate.split("T")[0] // format YYYY-MM-DD
                                                            : ""}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <InfoIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>Status:</span>{" "}
                                                        {selectedFarmDetails.details?.status}
                                                    </Typography>
                                                </Box>

                                                 <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <CiMoneyCheck1 sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>Tax Amount (Ksh):</span>{" "}
                                                        {selectedFarmDetails?.tax_amount}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <CiMoneyCheck1 sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>Total Amount: (Ksh)</span>{" "}
                                                        {selectedFarmDetails?.total_amount}
                                                    </Typography>
                                                </Box>
                                               
                                                {/* <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <CropStageIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Crop Stage:
                                                        </span>{" "}
                                                        {selectedFarmDetails.details?.crop_stage}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <InfoIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Category:
                                                        </span>{" "}
                                                        {selectedFarmDetails.details?.category}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <InfoIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>Variety:</span>{" "}
                                                        {selectedFarmDetails.details?.variety}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <DateIcon sx={{ color: darkGreen, mr: 1 }} />
                                                    <Typography variant="body2">
                                                        <span style={{ fontWeight: "bold" }}>
                                                            Expired Date:
                                                        </span>{" "}
                                                        {selectedFarmDetails.details?.expired_date}
                                                    </Typography>
                                                </Box> */}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button
                                onClick={() => setOpenViewDialog(false)}
                                variant="outlined"
                                sx={{ color: darkGreen, borderColor: darkGreen }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Update Farm Dialog */}
            <Dialog
                open={openUpdateDialog}
                onClose={() => setOpenUpdateDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: primaryGreen,
                        color: "white",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <EditIcon sx={{ mr: 1 }} /> Update Farm:{" "}
                        {updateFormData.name || "N/A"}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenUpdateDialog(false)}
                        sx={{ color: "white" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Farm Name"
                                name="name"
                                value={updateFormData.name || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Total Area (acres)"
                                name="total_area"
                                value={updateFormData.total_area || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        {/* <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Soil Type"
                                name="soil_type"
                                value={updateFormData.soil_type || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid> */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Crop Type (comma separated)"
                                name="crop_type"
                                value={updateFormData.crop_type || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Location"
                                name="location"
                                value={updateFormData.details?.location || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sowing Date"
                                name="sowingdate"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={updateFormData.details?.sowingdate
                                    ? updateFormData.details.sowingdate.split("T")[0] // format YYYY-MM-DD
                                    : ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>

                        {/* import { MenuItem, TextField, Grid } from "@mui/material"; */}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                name="status"
                                value={updateFormData.details?.status || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            >
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Rejected">Rejected</MenuItem>
                            </TextField>
                        </Grid>


                        {/* <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Status"
                                name="status"
                                value={updateFormData.details?.status || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid> */}
                        {/* <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Crop Stage"
                                name="crop_stage"
                                value={updateFormData.details?.crop_stage || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Category"
                                name="category"
                                value={updateFormData.details?.category || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Variety"
                                name="variety"
                                value={updateFormData.details?.variety || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Expired Date"
                                name="expired_date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={updateFormData.details?.expired_date || ""}
                                onChange={handleFormChange}
                                margin="normal"
                                variant="outlined"
                            />
                        </Grid> */}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenUpdateDialog(false)}
                        variant="outlined"
                        sx={{ color: darkGreen, borderColor: darkGreen }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateFarm}
                        variant="contained"
                        sx={{
                            backgroundColor: primaryGreen,
                            "&:hover": { backgroundColor: darkGreen },
                        }}
                    >
                        Update Farm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteConfirm}
                onClose={() => setOpenDeleteConfirm(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        backgroundColor: "#f44336",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <DeleteIcon sx={{ mr: 1 }} /> Confirm Deletion
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Typography>
                        Are you sure you want to delete the farm "
                        <span style={{ fontWeight: "bold" }}>{farmToDelete?.name}</span>
                        "? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenDeleteConfirm(false)}
                        variant="outlined"
                        sx={{ color: "#f44336", borderColor: "#f44336" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteFarm}
                        variant="contained"
                        sx={{ backgroundColor: "#f44336", "&:hover": { backgroundColor: "#d32f2f" } }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FarmPrecisionPage;